import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { flashcardApi, playlistApi, tagApi } from '../utils/api';

// ── Drag-to-reorder helpers ───────────────────────────────────────────────────

function reorder(list, from, to) {
  if (from === to) return list;
  const next = [...list];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}

// ── DraggableItem ─────────────────────────────────────────────────────────────

function DraggableItem({ index, onDragStart, onDragOver, onDrop, onRemove, children }) {
  return (
    <div
      draggable
      onDragStart={() => onDragStart(index)}
      onDragOver={e => { e.preventDefault(); onDragOver(index); }}
      onDrop={() => onDrop(index)}
      className="d-flex align-items-center gap-2 p-2 mb-1 rounded border"
      style={{ cursor: 'grab', background: 'var(--bs-body-bg)', userSelect: 'none' }}
    >
      <span className="text-muted" style={{ cursor: 'grab', fontSize: '1.1rem' }}>⠿</span>
      <span className="flex-grow-1 small">{children}</span>
      <button
        type="button"
        className="btn btn-sm btn-outline-danger py-0 px-1"
        onClick={onRemove}
        title="Remove"
        style={{ lineHeight: 1.3 }}
      >
        ✕
      </button>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function PlaylistEditPage() {
  const { id }          = useParams();          // undefined when creating
  const isEdit          = Boolean(id);
  const { token }       = useAuth();
  const navigate        = useNavigate();

  // ── form state ──
  const [title, setTitle]             = useState('');
  const [shared, setShared]           = useState(false);
  const [allowRandom, setAllowRandom] = useState(false);
  const [selected, setSelected]       = useState([]); // [{flashcard_id, name, tags:[]}]

  // ── search state ──
  const [allCards, setAllCards]   = useState([]);
  const [allTags, setAllTags]     = useState([]);
  const [cardTagMap, setCardTagMap] = useState({}); // flashcard_id -> [tagName]
  const [query, setQuery]         = useState('');
  const [filterTag, setFilterTag] = useState('');

  // ── ui state ──
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');

  // ── drag state ──
  const dragIndex = useRef(null);

  // ── Load flashcards + tags, and playlist if editing ──────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cards, tags, tagInfos] = await Promise.all([
        flashcardApi.list(token),
        tagApi.list(token),
        tagApi.listAllForUser(token),
      ]);
      const cardArr = cards || [];
      const tagArr  = tags  || [];
      const infoArr = tagInfos || [];

      // Build flashcard_id -> [tagName] map
      const ctMap = {};
      for (const info of infoArr) {
        if (!ctMap[info.flashcard_id]) ctMap[info.flashcard_id] = [];
        ctMap[info.flashcard_id].push(info.tag);
      }

      setAllCards(cardArr);
      setAllTags(tagArr);
      setCardTagMap(ctMap);

      if (isEdit) {
        const pl = await playlistApi.get(id, token);
        setTitle(pl.title || '');
        setShared(pl.shared || false);
        setAllowRandom(pl.allow_random || false);
        // items are sorted by sort_order from the server
        const items = (pl.items || []).map(item => {
          const card = cardArr.find(c => c.id === item.flashcard_id);
          return {
            flashcard_id: item.flashcard_id,
            name: card ? card.name : `Card #${item.flashcard_id}`,
            tags: ctMap[item.flashcard_id] || [],
          };
        });
        setSelected(items);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, id, isEdit]);

  useEffect(() => { load(); }, [load]);

  // ── Search results ────────────────────────────────────────────────────────────
  const selectedIds = new Set(selected.map(s => s.flashcard_id));

  const searchResults = allCards.filter(card => {
    if (selectedIds.has(card.id)) return false;
    const cardTags = cardTagMap[card.id] || [];
    const matchesTag = filterTag
      ? cardTags.some(t => t.toLowerCase() === filterTag.toLowerCase())
      : true;
    const matchesQuery = query
      ? card.name.toLowerCase().includes(query.toLowerCase()) ||
        cardTags.some(t => t.toLowerCase().includes(query.toLowerCase()))
      : true;
    return matchesTag && matchesQuery;
  });

  // ── Add / remove ─────────────────────────────────────────────────────────────
  const addCard = (card) => {
    setSelected(prev => [
      ...prev,
      {
        flashcard_id: card.id,
        name: card.name,
        tags: cardTagMap[card.id] || [],
      },
    ]);
  };

  const removeCard = (index) => {
    setSelected(prev => prev.filter((_, i) => i !== index));
  };

  // ── Drag handlers ─────────────────────────────────────────────────────────────
  const handleDragStart = (index) => { dragIndex.current = index; };

  const handleDragOver = (index) => {
    if (dragIndex.current === null || dragIndex.current === index) return;
    setSelected(prev => reorder(prev, dragIndex.current, index));
    dragIndex.current = index;
  };

  const handleDrop = () => { dragIndex.current = null; };

  // ── Save ──────────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!title.trim()) { setError('Please enter a title.'); return; }
    setSaving(true);
    setError('');
    try {
      const items = selected.map((s, i) => ({
        flashcard_id: s.flashcard_id,
        sort_order:   i,
      }));
      if (isEdit) {
        await playlistApi.update(id, { title: title.trim(), shared, allow_random: allowRandom, items }, token);
      } else {
        await playlistApi.create({ title: title.trim(), shared, allow_random: allowRandom, items }, token);
      }
      navigate('/playlists');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-center mt-5"><div className="spinner-border" /></div>;

  return (
    <>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0">{isEdit ? 'Edit Playlist' : 'New Playlist'}</h2>
        <button className="btn btn-link" onClick={() => navigate('/playlists')}>
          ← Back to Playlists
        </button>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {/* Title */}
      <div className="mb-3" style={{ maxWidth: 480 }}>
        <label className="form-label fw-semibold">Title</label>
        <input
          className="form-control"
          value={title}
          onChange={e => setTitle(e.target.value)}
          placeholder="My playlist…"
          maxLength={100}
        />
      </div>

      {/* Options */}
      <div className="mb-4 d-flex gap-4">
        <div className="form-check">
          <input
            id="chk-shared"
            type="checkbox"
            className="form-check-input"
            checked={shared}
            onChange={e => setShared(e.target.checked)}
          />
          <label htmlFor="chk-shared" className="form-check-label">Shared</label>
        </div>
        <div className="form-check">
          <input
            id="chk-allow-random"
            type="checkbox"
            className="form-check-input"
            checked={allowRandom}
            onChange={e => setAllowRandom(e.target.checked)}
          />
          <label htmlFor="chk-allow-random" className="form-check-label">Allow random order</label>
        </div>
      </div>

      {/* Two-column layout */}
      <div className="row g-4">

        {/* ── LEFT: Search panel ─────────────────────────────────────── */}
        <div className="col-lg-6">
          <div className="card h-100">
            <div className="card-header fw-semibold">
              Search Flashcards
            </div>
            <div className="card-body d-flex flex-column gap-2">
              {/* Search controls */}
              <input
                className="form-control form-control-sm"
                placeholder="Search by name or tag…"
                value={query}
                onChange={e => setQuery(e.target.value)}
              />
              <select
                className="form-select form-select-sm"
                value={filterTag}
                onChange={e => setFilterTag(e.target.value)}
              >
                <option value="">All tags</option>
                {allTags.map(t => (
                  <option key={t.id} value={t.tag}>{t.tag}</option>
                ))}
              </select>

              {/* Results */}
              <div
                className="overflow-auto mt-1"
                style={{ maxHeight: 420, minHeight: 120 }}
              >
                {searchResults.length === 0 ? (
                  <div className="text-muted small mt-2 ps-1">
                    {allCards.length === 0
                      ? 'No flashcards found.'
                      : selectedIds.size === allCards.length
                      ? 'All cards have been added.'
                      : 'No cards match your search.'}
                  </div>
                ) : (
                  searchResults.map(card => (
                    <div
                      key={card.id}
                      className="d-flex align-items-center gap-2 p-2 mb-1 rounded border"
                      style={{ background: 'var(--bs-body-bg)' }}
                    >
                      <span className="flex-grow-1 small">
                        {card.name}
                        {(cardTagMap[card.id] || []).length > 0 && (
                          <span className="ms-2">
                            {(cardTagMap[card.id] || []).map(t => (
                              <span key={t} className="badge bg-secondary me-1" style={{ fontSize: '0.65rem' }}>{t}</span>
                            ))}
                          </span>
                        )}
                      </span>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-success py-0 px-2"
                        onClick={() => addCard(card)}
                        style={{ lineHeight: 1.4, whiteSpace: 'nowrap' }}
                      >
                        + Add
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ── RIGHT: Selected (orderable) ─────────────────────────────── */}
        <div className="col-lg-6">
          <div className="card h-100">
            <div className="card-header d-flex justify-content-between align-items-center">
              <span className="fw-semibold">Playlist Order</span>
              <span className="badge bg-secondary">{selected.length} card{selected.length !== 1 ? 's' : ''}</span>
            </div>
            <div className="card-body">
              {selected.length === 0 ? (
                <div className="text-muted small mt-1">
                  Add cards from the search panel on the left. Drag to reorder.
                </div>
              ) : (
                <div>
                  {selected.map((item, index) => (
                    <DraggableItem
                      key={item.flashcard_id}
                      index={index}
                      onDragStart={handleDragStart}
                      onDragOver={handleDragOver}
                      onDrop={handleDrop}
                      onRemove={() => removeCard(index)}
                    >
                      <span className="text-muted me-1" style={{ fontSize: '0.75rem' }}>{index + 1}.</span>
                      {item.name}
                      {item.tags.length > 0 && (
                        <span className="ms-2">
                          {item.tags.map(t => (
                            <span key={t} className="badge bg-secondary me-1" style={{ fontSize: '0.65rem' }}>{t}</span>
                          ))}
                        </span>
                      )}
                    </DraggableItem>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Save / Cancel */}
      <div className="d-flex gap-2 mt-4">
        <button
          className="btn btn-primary"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving…' : (isEdit ? 'Save Changes' : 'Create Playlist')}
        </button>
        <button className="btn btn-outline-secondary" onClick={() => navigate('/playlists')}>
          Cancel
        </button>
      </div>
    </>
  );
}
